import { useEffect, useMemo, useRef, useState } from 'react'
import { collection, getDocs, limit, query, where } from 'firebase/firestore'
import { firestoreDb } from '../../../../services/firebase'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeToDateRange } from './bucketize'

export type UseAutomationHealthModuleParams = {
  workspaceId: string
  timeRange?: AnalysisTimeRangeKey
  refreshKey?: number
  now?: Date
}

export type UseAutomationHealthModuleResult = {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

type AutomationRunRow = {
  id: string
  automationId: string
  outcome: string
  timestamp: Date | null
}

const parseDate = (value: unknown): Date | null => {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') return (value as { toDate: () => Date }).toDate()
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }
  return null
}

export const useAutomationHealthModule = ({
  workspaceId,
  timeRange = '7D',
  refreshKey,
  now = new Date(),
}: UseAutomationHealthModuleParams): UseAutomationHealthModuleResult => {
  const [runs, setRuns] = useState<AutomationRunRow[]>([])
  const [namesByAutomationId, setNamesByAutomationId] = useState<Map<string, string>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const activeRequest = useRef(0)

  useEffect(() => {
    if (!workspaceId) {
      setRuns([])
      setNamesByAutomationId(new Map())
      setLoading(false)
      setError(null)
      return undefined
    }

    const requestId = ++activeRequest.current
    setLoading(true)
    setError(null)

    // Deliberate, lightweight scope creep: definitions make run records intelligible without changing run derivations.
    void Promise.all([
      getDocs(query(collection(firestoreDb, 'automation_runs'), where('workspaceId', '==', workspaceId), limit(50))),
      getDocs(query(collection(firestoreDb, 'automation_definitions'), where('workspaceId', '==', workspaceId), limit(50))),
    ])
      .then(([runsSnapshot, definitionsSnapshot]) => {
        if (requestId !== activeRequest.current) return

        setRuns(runsSnapshot.docs.map((entry) => {
          const data = entry.data()
          return {
            id: entry.id,
            automationId: String(data.automationId ?? ''),
            outcome: String(data.outcome ?? data.status ?? ''),
            timestamp: parseDate(data.startedAt) ?? parseDate(data.completedAt) ?? parseDate(data.finishedAt),
          }
        }).filter((run) => Boolean(run.automationId)))
        setNamesByAutomationId(new Map(definitionsSnapshot.docs.map((entry): [string, string] => {
          const data = entry.data()
          return [entry.id, typeof data.name === 'string' ? data.name : '']
        }).filter(([, name]) => Boolean(name))))
        setLoading(false)
      })
      .catch((reason) => {
        if (requestId !== activeRequest.current) return
        setRuns([])
        setNamesByAutomationId(new Map())
        setError(reason instanceof Error ? reason : new Error('Failed to load automation runs'))
        setLoading(false)
      })

    return () => {
      activeRequest.current += 1
    }
  }, [workspaceId, refreshKey])

  return useMemo(() => {
    const { from, to } = timeRangeToDateRange(timeRange, now)
    const inRangeRuns = runs.filter((run) => run.timestamp && run.timestamp >= from && run.timestamp <= to)

    if (inRangeRuns.length === 0) {
      return {
        headline: { value: 0, label: 'runs' },
        sparkline: { points: [], direction: 'up-is-bad' },
        records: [],
        loading,
        error,
      }
    }

    const grouped = new Map<string, { runCount: number; failureCount: number; mostRecent: Date }>()
    for (const run of inRangeRuns) {
      const current = grouped.get(run.automationId)
      const timestamp = run.timestamp as Date
      grouped.set(run.automationId, {
        runCount: (current?.runCount ?? 0) + 1,
        failureCount: (current?.failureCount ?? 0) + (run.outcome.toUpperCase() === 'FAILED' ? 1 : 0),
        mostRecent: !current || timestamp > current.mostRecent ? timestamp : current.mostRecent,
      })
    }

    const records: LiveModuleRecord[] = [...grouped.entries()]
      .sort(([, left], [, right]) => right.mostRecent.getTime() - left.mostRecent.getTime())
      .slice(0, 5)
      .map(([automationId, activity]) => ({
        id: `automation-${automationId}`,
        primary: namesByAutomationId.get(automationId) || `Automation ${automationId.slice(0, 6)}`,
        secondary: `${activity.runCount} runs · ${activity.failureCount} failed`,
        numeric: activity.runCount,
        drillTarget: 'AUTOMATE',
      }))

    // Outcomes are available, so the chart tracks failures: an upward trend is operationally bad.
    const failureDates = inRangeRuns
      .filter((run) => run.outcome.toUpperCase() === 'FAILED')
      .map((run) => run.timestamp as Date)
    const points = bucketize(failureDates, timeRange, now)

    return {
      headline: { value: inRangeRuns.length, label: 'runs', delta: calculateDelta(points) },
      sparkline: { points, direction: 'up-is-bad' },
      records,
      loading,
      error,
    }
  }, [runs, namesByAutomationId, timeRange, now, loading, error])
}
